import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './NewLoginScreen.css';

// Asset Imports
import etmIcon from '../../assets/img/etm_icon.png';
import travelAgentIcon from '../../assets/img/TravelAgent_icon.png';
import corporateIcon from '../../assets/img/Corporate_Icon.png';
import apiIcon from '../../assets/img/API_icon.png';

// Service & Action Imports
import { useDispatch } from 'react-redux';
import { loaderActions } from '../../_actions/loader.actions';
import { SetLocalStorage } from '../../components/commonClass/Session';
import { getAllData, getDataByPara, getOtpPostReq } from '../../services/ApiCallingUserServices';
import { LoginModel } from '../../components/admin/modals/LoginModel';

const DomainType = {
    Internal: 1,
    TravelAgent: 2,
    Corporate: 3,
    API: 4
} as const;

type LoginType = typeof DomainType[keyof typeof DomainType];

interface LoginForm {
    DomainTypeId: LoginType;
    userType: number;
    UserEmail: string;
    UserName: string;
    OTP: string;
    Password: string;
}


const NewLoginScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    // State Management
    const [loginType, setLoginType] = useState<LoginType>(DomainType.Internal);
    const [loginform, setLoginForm] = useState(new LoginModel());
    const [isOtpStep, setIsOtpStep] = useState(false);
    const [otp, setOtp] = useState(['', '', '', '']);
    const [timer, setTimer] = useState(30);
    const [showToast, setShowToast] = useState(false);
    const [toastMsg, setToastMsg] = useState('');
    const [formErrors, setFormErrors] = useState<any>({});
    const [disabled, setDisabled] = useState(false);

    const otpRef0 = useRef<HTMLInputElement>(null);
    const otpRef1 = useRef<HTMLInputElement>(null);
    const otpRef2 = useRef<HTMLInputElement>(null);
    const otpRef3 = useRef<HTMLInputElement>(null);
    const otpRefs = [otpRef0, otpRef1, otpRef2, otpRef3];

    // Timer Logic
    useEffect(() => {
        let interval: any;
        if (isOtpStep && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isOtpStep, timer]);

    // Sync UserType in loginform
    useEffect(() => {
        setLoginForm(prev => ({ ...prev, DomainTypeId: loginType }));
    }, [loginType]);

    // Handle Dynamic Routing
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const typeParam = params.get('type')?.toLowerCase();

        if (typeParam === 'travelagent') {
            handleLoginTypeChange(DomainType.TravelAgent);
        } else if (typeParam === 'corporateagent') {
            handleLoginTypeChange(DomainType.Corporate);
        } else {
            handleLoginTypeChange(DomainType.Internal);
        }
    }, [location.search]);

    // Handle OTP Timeout
    useEffect(() => {
        if (isOtpStep && timer === 0) {
            handleOtpExpire();
        }
    }, [isOtpStep, timer]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormErrors({ ...formErrors, [name]: '' });
        setLoginForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const getUserById = async (userId: any) => {
        const userdata = await getAllData('/auth/v1/getuserrightsforvisibility?UserID=' + userId);
        if (userdata && userdata.data) {
            dispatch({ type: 'USERDATA', tba: userdata.data });
        }
    }

    const handleOtpExpire = () => {
        setToastMsg('OTP expired. Please resend.');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!loginform.UserEmail) {
            setFormErrors({ UserEmail: 'Please provide email ID' });
            return;
        }

        if (!loginform.UserEmail.includes('@')) {
            setFormErrors({ UserEmail: 'Please provide valid email Id' });
            return;
        }

        dispatch(loaderActions.start());
        try {
            const payload = {
                UserEmail: loginform.UserEmail,
                OtpType: 1,
                DomainMasterId: loginform.DomainTypeId,
                UserType: 3
            };
            const response = await getOtpPostReq('/auth/v1/generate-otp', payload);

            dispatch(loaderActions.end());
            if (response.status) {
                setIsOtpStep(true);
                setTimer(30);
                setToastMsg('OTP sent successfully');
                setShowToast(true);
                setTimeout(() => setShowToast(false), 3000);
            } else {
                setFormErrors({ UserEmail: response.message || 'Failed to send OTP' });
            }
        } catch (error) {
            dispatch(loaderActions.end());
            setFormErrors({ UserEmail: 'Something went wrong. Please try again.' });
        }
    };

    const handleInternalLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!loginform.UserName || !loginform.Password) {
            setFormErrors({
                UserName: !loginform.UserName ? 'Username is required' : '',
                Password: !loginform.Password ? 'Password is required' : ''
            });
            return;
        }

        dispatch(loaderActions.start());
        setDisabled(true);

        const loginModel = {
            username: loginform.UserName,
            password: loginform.Password,
            userType: 3,
            DomainTypeId: loginform.DomainTypeId
        };

        try {
            const userData = await getDataByPara('/auth/v1/login-pass', loginModel);

            if (userData.status) {
                SetLocalStorage(userData.data);
                if (userData.data && userData.data.userMasterID) {
                    await getUserById(userData.data.userMasterID);
                }

                dispatch(loaderActions.end());
                navigate('/dashboard', { state: { username: userData.data?.username || loginform.UserName } });
            } else {
                dispatch(loaderActions.end());
                setFormErrors({ credentials: userData.message || "Please enter correct Username or Password" });
                setDisabled(false);
            }
        } catch (error) {
            dispatch(loaderActions.end());
            setDisabled(false);
            setFormErrors({ credentials: "An error occurred during login" });
        }
    };

    const handleOtpChange = (index: number, value: string) => {
        // Only allow numbers
        const lastChar = value.substring(value.length - 1);
        if (lastChar && isNaN(Number(lastChar))) return;

        const newOtp = [...otp];
        newOtp[index] = lastChar;
        setOtp(newOtp);

        // Move to next field if value entered
        if (lastChar && index < 3) {
            otpRefs[index + 1].current?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace') {
            if (!otp[index] && index > 0) {
                // If current is empty, move back and clear previous
                const newOtp = [...otp];
                newOtp[index - 1] = '';
                setOtp(newOtp);
                otpRefs[index - 1].current?.focus();
            } else if (otp[index]) {
                // If current has value, just clear it
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
            }
        }
    };

    const handleResendOtp = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (timer > 0) return;

        if (!loginform.UserEmail) {
            setToastMsg('Email is required');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
            return;
        }

        dispatch(loaderActions.start());
        try {
            const payload = {
                UserEmail: loginform.UserEmail,
                OtpType: 1,
                DomainMasterId: loginform.DomainTypeId,
                UserType: 3
            };
            const response = await getOtpPostReq('/auth/v1/generate-otp', payload);

            dispatch(loaderActions.end());
            if (response.status) {
                setTimer(30);
                setOtp(['', '', '', '']);
                setToastMsg('OTP resent successfully');
                setShowToast(true);
                setTimeout(() => setShowToast(false), 3000);
            } else {
                setToastMsg(response.message || 'Failed to resend OTP');
                setShowToast(true);
                setTimeout(() => setShowToast(false), 3000);
            }
        } catch (error) {
            dispatch(loaderActions.end());
            setToastMsg('Something went wrong. Please try again.');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        const otpValue = otp.join('');
        if (otpValue.length < 4) {
            setToastMsg('Please enter valid OTP');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
            return;
        }

        dispatch(loaderActions.start());
        try {
            // Verify OTP
            const verifyPayload = {
                UserEmail: loginform.UserEmail,
                OTP: otpValue,
                UserType: 3,
                DomainMasterId: loginform.DomainTypeId,
                OtpType: 1
            };
            const verifyRes = await getDataByPara('/auth/v1/verify-mo-otp', verifyPayload);

            if (verifyRes.status) {
                SetLocalStorage(verifyRes.data);
                if (verifyRes.data && verifyRes.data.userMasterID) {
                    await getUserById(verifyRes.data.userMasterID);
                }
                dispatch(loaderActions.end());
                navigate('/dashboard', { state: { username: verifyRes.data?.username || loginform.UserEmail } });
            } else {
                dispatch(loaderActions.end());
                setToastMsg(verifyRes.message || 'Invalid OTP');
                setShowToast(true);
                setTimeout(() => setShowToast(false), 3000);
            }
        } catch (error) {
            dispatch(loaderActions.end());
            setToastMsg('Verification failed');
            setShowToast(true);
        }
    };

    const LogintytpeList = [
        {
            Id: DomainType.Internal,
            Name: 'ETM Login',
            Icon: etmIcon,
        },
        {
            Id: DomainType.TravelAgent,
            Name: 'Travel Agent Login',
            Icon: travelAgentIcon,
        },
        {
            Id: DomainType.Corporate,
            Name: 'Corporate Login',
            Icon: corporateIcon,
        },
        {
            Id: DomainType.API,
            Name: 'API Login',
            Icon: apiIcon,
            description: 'Coming soon',
            disabled: true
        }
    ];

    const handleLoginTypeChange = (type: LoginType) => {
        setLoginType(type);
        setIsOtpStep(false);
        setLoginForm({ ...new LoginModel(), DomainTypeId: type });
        setFormErrors({});
        setOtp(['', '', '', '']);

        // Update URL query parameter
        let typeSlug = '';
        if (type === DomainType.TravelAgent) {
            typeSlug = 'travelagent';
        } else if (type === DomainType.Corporate) {
            typeSlug = 'corporateagent';
        } else {
            typeSlug = 'etm';
        }

        navigate(`/?type=${typeSlug}`, { replace: true });
    };

    return (
        <div className='new-login-main'>
            <div className="new-login-wrapper">
                {showToast && <div className="otp-toast">{toastMsg}</div>}
                <div className="main-content-layout">
                    {/* Left Panel: Brand & Tagline */}
                    <div className="left-information-panel">
                        <div className="top-brand-content">
                            <div className="brand-section">
                                <a href="/"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        // window.location.replace("/");
                                        navigate("/");
                                    }}
                                >
                                    <img src={require("../../assets/img/login-screen-logo.png")} alt="logo" style={{ width: "200px", height: "auto", objectFit: "contain" }} />
                                </a>
                            </div>

                            <div className="tagline-container">
                                <h1 className="tagline-hero">
                                    BOOK YOUR NEXT TRAVEL<br />
                                    EXPERIENCE WITH ETM
                                </h1>
                            </div>
                            <p className="panel-description">
                                Travel Agent Portal - Manage bookings, access exclusive deals, and grow your travel business with ETM.
                            </p>
                        </div>

                        {/* Bottom Left: Selection Cards */}
                        <div className="login-selection-row">

                            {LogintytpeList.map((item) => (
                                <div key={item.Id}
                                    className={`selection-card ${loginType === item.Id ? 'active' : ''} ${item.disabled ? 'disabled' : ''}`}
                                    onClick={() => !item.disabled && handleLoginTypeChange(item.Id)}
                                >
                                    <div className="sel-icon-box">
                                        <img src={item.Icon} alt={item.Name} className="sel-img" />
                                    </div>
                                    <div className="sel-title">{item.Name}</div>
                                    <div className="sel-subtitle">{item.description}</div>
                                </div>
                            ))}

                        </div>
                    </div>

                    {/* Right Panel: Form Card */}
                    <div className="right-form-panel">
                        <div className="login-form-card">
                            {!isOtpStep ? (
                                <form onSubmit={loginType === DomainType.Internal ? handleInternalLogin : handleSendOtp}>
                                    <h2>{loginType === DomainType.Internal ? 'Login with ETM' : loginType === DomainType.TravelAgent ? 'Travel Agent Sign In' : 'Corporate Sign In'}</h2>

                                    {formErrors.credentials && <p className="text-danger small text-center">{formErrors.credentials}</p>}

                                    <div className={`input-group-custom ${formErrors.UserName || formErrors.UserEmail ? 'error' : ''}`}>
                                        <label>{loginType === DomainType.Internal ? 'Username*' : 'Email*'}</label>
                                        <div className="input-box">
                                            <i className={`bi ${loginType === DomainType.Internal ? 'bi-person' : 'bi-envelope'}`}></i>
                                            <input
                                                type="text"
                                                className="input-field"
                                                name={loginType === DomainType.Internal ? "UserName" : "UserEmail"}
                                                placeholder={loginType === DomainType.Internal ? 'Enter username' : 'agentname@gmail.com'}
                                                value={loginType === DomainType.Internal ? loginform.UserName : loginform.UserEmail}
                                                onChange={handleInputChange}
                                                maxLength={150}
                                                required
                                            />
                                        </div>
                                        {(formErrors.UserName || formErrors.UserEmail) && <p className="error-text text-danger small">{formErrors.UserName || formErrors.UserEmail}</p>}
                                    </div>

                                    {loginType === DomainType.Internal && (
                                        <div className={`input-group-custom ${formErrors.Password ? 'error' : ''}`}>
                                            <label>Password*</label>
                                            <div className="input-box">
                                                <i className="bi bi-lock"></i>
                                                <input
                                                    type="password"
                                                    name="Password"
                                                    className="input-field"
                                                    placeholder="********"
                                                    value={loginform.Password}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                            {formErrors.Password && <p className="error-text text-danger small">{formErrors.Password}</p>}
                                        </div>
                                    )}

                                    <button type="submit" className="form-action-btn" disabled={disabled}>
                                        {loginType === DomainType.Internal ? 'Login' : 'Send OTP'}
                                    </button>

                                    <div className="text-end mt-2">
                                        <a href="/ForgotPassword" onClick={(e) => { e.preventDefault(); navigate('/ForgotPassword'); }} className="small text-decoration-none text-muted">Forgot Password?</a>
                                    </div>
                                </form>
                            ) : (
                                <form onSubmit={handleLogin}>
                                    <h2>Verify OTP</h2>
                                    <p className="text-center text-muted small">Enter the 4-digit code sent to your email</p>

                                    <div className="otp-box-row">
                                        {otp.map((digit, i) => (
                                            <input
                                                key={i}
                                                ref={otpRefs[i]}
                                                type="text"
                                                className="otp-digit-input"
                                                value={digit}
                                                onChange={(e) => handleOtpChange(i, e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(i, e)}
                                                maxLength={1}
                                            />
                                        ))}
                                    </div>

                                    <button type="submit" className="form-action-btn">Submit OTP</button>

                                    <div className="otp-timer-info">
                                        <span>Didn't receive it? <a href="#" onClick={handleResendOtp} className={`otp-timer-highlight text-decoration-none ${timer > 0 ? 'disabled' : ''}`}>Resend OTP</a></span>
                                        {timer > 0 && <span className="ms-2 opacity-50">({timer}s)</span>}
                                    </div>
                                </form>
                            )}

                            <div className="terms-link-block">
                                By continuing you agree to our <a href="#">Terms & Conditions</a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewLoginScreen;
